/**
 * DiffHopInputs.jsx
 * Right-panel controls specific to the DiffHop scaffold-hopping workflow.
 *
 * Props:
 *   ligandSource     'auto' | 'upload'
 *   onLigandSource   fn(val)
 *   sdfFile          File | null
 *   onSdfFile        fn(File | null)
 *   nMolecules       number
 *   onNMolecules     fn(number)
 *   sdfInputRef      ref forwarded from parent
 */

import React from 'react';
import { Upload, FlaskConical, CheckCircle2, X, Info } from 'lucide-react';
import { s, ToggleBtn } from './genchemShared';

export default function DiffHopInputs({
  ligandSource,
  onLigandSource,
  sdfFile,
  onSdfFile,
  nMolecules,
  onNMolecules,
  sdfInputRef,
}) {
  function handleLigandChange(val) {
    onLigandSource(val);
    if (val === 'auto') {
      onSdfFile(null);
      if (sdfInputRef.current) sdfInputRef.current.value = '';
    }
  }

  function handleNChange(e) {
    const val = Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 1));
    onNMolecules(val);
  }

  return (
    <>
      {/* ── Ligand source ── */}
      <div style={s.card}>
        <p style={s.cardLabel}>Ligand source</p>
        <p style={s.cardSubLabel}>
          Ligand will be auto selected for the scaffold-hopping model.
        </p>
        <div style={s.toggleRow}>
          <ToggleBtn
            active={ligandSource === 'auto'}
            onClick={() => handleLigandChange('auto')}
            icon={<FlaskConical size={14} strokeWidth={1.8} />}
            label="Auto ligand"
          />
          {/* <ToggleBtn
            active={ligandSource === 'upload'}
            onClick={() => handleLigandChange('upload')}
            icon={<Upload size={14} strokeWidth={1.8} />}
            label="Upload scaffold"
          /> */}
        </div>

        {ligandSource === 'auto' && (
          <div style={s.infoNote}>
            <Info size={12} color="#7c5cbf" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              The highest-occupancy heteroatom ligand in the PDB structure will be
              automatically selected as the reference scaffold for hopping.
            </span>
          </div>
        )}

        {ligandSource === 'upload' && (
          <div style={s.uploadArea} onClick={() => sdfInputRef.current?.click()}>
            <input
              ref={sdfInputRef}
              type="file"
              accept=".sdf,.mol"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onSdfFile(file);
              }}
            />
            {sdfFile ? (
              <div style={s.fileRow}>
                <CheckCircle2 size={14} color="#7c5cbf" />
                <span style={s.fileName}>{sdfFile.name}</span>
                <button
                  style={s.clearBtn}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onSdfFile(null);
                    if (sdfInputRef.current) sdfInputRef.current.value = '';
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div style={s.uploadPrompt}>
                <Upload size={15} color="#9d84d8" strokeWidth={1.6} />
                <span style={s.uploadText}>
                  Upload reference scaffold{' '}
                  <span style={s.uploadExt}>.sdf / .mol</span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Molecules to generate ── */}
      <div style={s.card}>
        <div style={s.cardHeaderRow}>
          <p style={s.cardLabel}>Scaffolds to generate</p>
          <span style={s.badge}>{nMolecules}</span>
        </div>
        <p style={s.cardSubLabel}>
          Number of novel scaffolds the model will generate around the
          detected pocket. Higher counts increase diversity but extend runtime.
        </p>
        <input
          style={{
            ...s.numberInput,
            borderColor: nMolecules < 1 || nMolecules > 500 ? '#e05252' : '#e2ddf0',
          }}
          type="number"
          min={1}
          max={500}
          value={nMolecules}
          onChange={handleNChange}
        />
        <p style={s.inputHint}>Range: 1 – 500 molecules per job</p>
      </div>
    </>
  );
}